import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Upload, Image, Button, notification, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import DebouncedSelect from "../../Component/DebouncedSelect";
import { getAllRecommendations } from "../../Services/recommendation";

const EditModal = ({
  isModalVisible,
  handleOk,
  handleCancel,
  loading,
  initialValues,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: recommendationsData, isLoading: recommendationsLoading } = useQuery({
    queryKey: ["recommendations_all_for_destination_edit", isModalVisible],
    queryFn: () => getAllRecommendations({ skip: 0, limit: 1000 }),
    enabled: isModalVisible,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const recommendationOptions = (recommendationsData?.recommendations || []).map((r) => ({
    label: r.name,
    value: r.id,
  }));

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        destination_text: initialValues.destination_text,
        destination_address: initialValues.destination_address,
        recommendation_ids: initialValues.recommendation_ids || [],
      });
      //   form.setFieldsValue({ image: fileList.length > 0 ? fileList[0] : null });

      if (initialValues.full_image_url) {
        // Ensure you're using the correct image key
        const fileData = {
          uid: "-1",
          name: "image",
          status: "done",
          url: initialValues.full_image_url,
        };
        setFileList([fileData]);
        form.setFieldsValue({ image: fileData });
      } else {
        setFileList([]);
      }
    }
  }, [initialValues, form, isModalVisible]);

  const handleChange = ({ fileList }) => {
    setFileList(fileList.slice(-1));
    form.setFieldsValue({ image: fileList.length > 0 ? fileList[0] : null });
  };

  const handlePreview = async (file) => {
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Select</div>
    </div>
  );

  //   const onFinish = (values) => {
  //     handleOk({ ...values, image: fileList[0]?.originFileObj || initialValues.image || null });
  //     form.resetFields();
  //     setFileList([]);
  //     setPreviewImage("");
  //   };

  const onFinish = async (values) => {
    const updatedValues = {};

    // Check for changed fields
    Object.keys(values).forEach((key) => {
      if (key === "image" || key === "recommendation_ids") return;
      if (values[key] !== initialValues[key]) {
        updatedValues[key] = values[key];
      }
    });

    // Compare recommendation_ids as arrays
    const newRecIds = values.recommendation_ids || [];
    const oldRecIds = initialValues?.recommendation_ids || [];
    const sameRecIds =
      newRecIds.length === oldRecIds.length &&
      [...newRecIds].sort().every((v, i) => v === [...oldRecIds].sort()[i]);
    if (!sameRecIds) {
      updatedValues.recommendation_ids = newRecIds;
    }

    // Handle image separately
    if (fileList.length > 0 && fileList[0]?.originFileObj) {
      updatedValues.image = fileList[0].originFileObj;
    }

    // Only proceed if there's something to update
    if (Object.keys(updatedValues).length > 0) {
      await handleOk(initialValues?.id, updatedValues);
    } else {
      notification.error({
        message: "No changes detected",
        duration: 2,
      });
    }
  };

  return (
    <Modal
      title="Edit Recommended Destination"
      open={isModalVisible}
      footer={null}
      onCancel={() => {
        handleCancel();
        form.resetFields();
        setFileList([]);
        setPreviewImage("");
      }}
      maskClosable={false}
      style={{ top: 10 }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="destination_text"
          label="Destination Place"
          rules={[
            { required: true, message: "Please input the Destination Place!" },
          ]}
        >
          <Input placeholder="Enter Destination Place" />
        </Form.Item>
        <Form.Item
          name="destination_address"
          label="Destination Location"
          rules={[
            {
              required: true,
              message: "Please select an Destination Location",
            },
          ]}
        >
          <DebouncedSelect
            placeholder="Type at least 4 characters to search address"
            value={form.getFieldValue("destination_address")}
            // onChange={(newValue) => form.setFieldsValue({ destination_address: newValue })}
          />
        </Form.Item>
        <Form.Item
          name="recommendation_ids"
          label="Recommendations"
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Select recommendations"
            loading={recommendationsLoading}
            options={recommendationOptions}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </Form.Item>
        <Form.Item
          name="image"
          label="Select Image"
          rules={[{ required: true, message: "Please upload an image" }]}
        >
          <Upload
            listType="picture-card"
            fileList={fileList}
            onPreview={handlePreview}
            onChange={handleChange}
            beforeUpload={() => false}
            maxCount={1}
            accept=".jpg,.jpeg,.png,.svg"
            onRemove={async (file) => {
              return new Promise((resolve, reject) => {
                Modal.confirm({
                  title: "Are you sure you want to delete this image?",
                  okText: "Yes",
                  cancelText: "No",
                  onOk: () => {
                    resolve(true); // allow deletion
                  },
                  onCancel: () => {
                    resolve(false); // cancel deletion
                  },
                });
              });
            }}
          >
            {fileList.length >= 1 ? null : uploadButton}
          </Upload>
          <small>Recommended sizes: 640×360 px, 1280×720 px</small>
          <br />
          <small>Aspect ratio: 16:9</small>
          <br />
          <small>File size: 50–200 KB</small>
          {previewImage && (
            <Image
              wrapperStyle={{ display: "none" }}
              preview={{
                visible: previewOpen,
                onVisibleChange: (visible) => setPreviewOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(""),
                toolbarRender: () => null,
              }}
              src={previewImage}
            />
          )}
        </Form.Item>
        <Form.Item>
          <div className="flex items-center justify-end gap-4">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Changes
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditModal;
